import React, { useMemo } from "react";

import {
  Box,
  Loader,
  RoundedBox,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useUser } from "@js/apps/common/hooks";
import { useEmployerProfilePage } from "@js/apps/employer/hooks";
import { WithJobActionsContext } from "@js/apps/jobs/components/job-actions";
import { JobItem } from "@js/apps/jobs/components/job-item";
import { JobsContainer } from "@js/apps/jobs/components/listing/jobs-container";
import { JobLocationContext } from "@js/apps/jobs/context";
import { PageHead } from "@js/components/page-head";
import { PageTitle } from "@js/components/page-title";
import { AppLayout } from "@js/layouts/app";
import { pluralize } from "@js/utils";

import { EmployerLogoWithName, EmployerOverview } from "../../components";

import { BasicInfo, EmployerReviews, JobsInfo, TopBar } from "./components";

import styles from "./style.module.scss";

export const EmployerProfilePage = () => {
  const { refetchEmployerPublicProfile, employerPublicProfile, openJobs } =
    useEmployerProfilePage();

  const user = useUser();

  const withJobActionsContextProviderValue = useMemo(
    () => ({
      afterAction: () => {
        void refetchEmployerPublicProfile();
      },
    }),
    [refetchEmployerPublicProfile],
  );

  const sortedOpenJobs = useMemo(() => {
    return [...openJobs].reverse();
  }, [openJobs]);

  if (!employerPublicProfile) {
    return <Loader centered />;
  }

  const isProfileOwner = employerPublicProfile.id === user?.employer;

  return (
    <AppLayout bgcolor="var(--soft-blue)" className="justify-center">
      <PageHead>
        <meta property="og:title" content={employerPublicProfile.name} />
        <meta property="og:image" content={employerPublicProfile.logo} />
        <meta
          property="og:description"
          content={employerPublicProfile.overview}
        />
        <meta name="description" content={employerPublicProfile.overview} />
      </PageHead>
      <PageTitle>{`Employer: ${employerPublicProfile.name}`}</PageTitle>
      <Box component="main" sx={{ width: "100%", maxWidth: 1100 }}>
        <Box sx={{ mb: 4 }}>
          <TopBar
            employer={employerPublicProfile}
            isProfileOwner={isProfileOwner}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <EmployerLogoWithName
            imageSource={employerPublicProfile.logo_thumbnail}
            employer={employerPublicProfile}
            gap={24}
            size="large"
            nameComponent="h1"
            nameVariant="title"
            nameSize="medium"
            color="var(--info-2)"
            fontWeight={400}
            noUrl
          />
        </Box>
        <Box
          component="section"
          className={styles.employerProfileInfo}
          sx={{ mb: 4 }}
        >
          <div className={styles.employerProfileCol1}>
            <div className={styles.employerProfileOverview}>
              <RoundedBox sx={{ height: 1 }}>
                <EmployerOverview
                  name={employerPublicProfile.name}
                  overview={employerPublicProfile.overview}
                />
              </RoundedBox>
            </div>
          </div>
          <div className={styles.employerProfileCol2}>
            <div className={styles.employerProfileMeta}>
              <BasicInfo
                website={employerPublicProfile.website}
                industry={employerPublicProfile.industry}
                location={employerPublicProfile.location}
              />
            </div>
            <div className={styles.employerProfileTotalJobs}>
              <JobsInfo
                label="Completed Jobs"
                value={employerPublicProfile.completed_jobs_number}
              />
            </div>
            <div className={styles.employerProfileHiredTalent}>
              <JobsInfo
                label="Hired Talent"
                value={employerPublicProfile.talent_hired_count}
              />
            </div>
          </div>
        </Box>
        <EmployerReviews employer={employerPublicProfile} />
        {!!openJobs?.length && (
          <Box component="section" sx={{ mt: 8 }}>
            <Typography
              component="h2"
              variant="title"
              size="small"
              fontWeight={400}
            >
              {employerPublicProfile.open_jobs_count} Open Job
              {pluralize(employerPublicProfile.open_jobs_count)}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <div>
                <JobLocationContext.Provider
                  value={JobLocationContext.Values.employer_profile}
                >
                  <WithJobActionsContext.Provider
                    value={withJobActionsContextProviderValue}
                  >
                    <JobsContainer sx={{ gap: 2 }}>
                      {sortedOpenJobs.map((job) => {
                        return <JobItem key={job.id} job={job} />;
                      })}
                    </JobsContainer>
                  </WithJobActionsContext.Provider>
                </JobLocationContext.Provider>
              </div>
            </Box>
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};
